import React, { useState } from 'react';
import { HeadCell } from '../models/headCell';

interface TableFunctions<T> {
  columnsData: HeadCell[];
  page: number;
  rowsPerPage: number;
  sortBy: string;
  sortType: 'asc' | 'desc';
  searchTerm: string;
  filterData: T;
  setColumnsData: React.Dispatch<React.SetStateAction<HeadCell[]>>;
  onRequestSort: (property: string) => void;
  onChangePage: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => void;
  onChangeRowsPerPage: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onChangeColumnVisibility: (
    column: HeadCell
  ) => (event: React.ChangeEvent<HTMLInputElement>) => HeadCell[];
  onSearchChange: (search: string) => void;
  onFilterChange: (data: T) => void;
}

export const useTableFunctions = <T>(
  initialColumnsData: HeadCell[],
  initialFilterData: T,
  defaultSortBy = '',
  defaultSortType: 'asc' | 'desc' = 'asc'
): TableFunctions<T> => {
  const [columnsData, setColumnsData] = useState<HeadCell[]>([
    ...initialColumnsData
  ]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(50);
  const [sortBy, setSortBy] = useState(defaultSortBy);
  const [sortType, setSortType] = useState<'asc' | 'desc'>(defaultSortType);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterData, setFilterData] = useState(initialFilterData);

  const onRequestSort = (property: string) => {
    const isAsc = sortBy === property && sortType === 'asc';
    setSortType(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  const onChangePage = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const onChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const onChangeColumnVisibility =
    (column: HeadCell) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const newColumnArray = columnsData.map((col) =>
        col.id === column.id
          ? { ...column, visible: event.target.checked }
          : col
      );
      setColumnsData(newColumnArray);
      return newColumnArray;
    };

  const onSearchChange = (search: string) => {
    setSearchTerm(search);
  };

  const onFilterChange = (data: T) => {
    if (JSON.stringify(filterData) !== JSON.stringify(data)) {
      setFilterData(data);
    }
  };

  return {
    columnsData,
    setColumnsData,
    page,
    rowsPerPage,
    sortBy,
    sortType,
    searchTerm,
    filterData,
    onRequestSort,
    onChangePage,
    onChangeRowsPerPage,
    onChangeColumnVisibility,
    onSearchChange,
    onFilterChange
  };
};
